import { AnimatePresence, motion } from "motion/react";
import React, { useCallback, useMemo, useState } from "react";
import { UI_MOTION } from "../../../config/uiMotion.configuration";
import { useReducedMotion } from "../../../hooks/useReducedMotion.hook";
import { isNavigationEnabled } from "../../../services/FrontendConfigApi";
import { gpsPredictionService } from "../../../services/navigation";
import { itineraryPanelStateSlice, navigationSessionSlice, useEphemeralSelector, type ItineraryPanelState, type PanelSnapTier } from "../../../store/ephemeral";
import { convertItineraryToSegments } from "../../../tools/NavigationTools";
import { hasPurchasedSegments } from "../../../tools/purchasedItineraries.tools";
import { type ItineraryDTO } from "../../../types/dtos/api/itinerary/itinerary.dto";
import { SegmentationProviderId } from "../../../types/itinerary/segmentation-provider.enum";
import { type ContainerProps } from "../../../types/ItineraryPlanner";
import { resolveUiEaseOut } from "../../shared/motion/uiMotion.tools";
import { itineraryHasNullSegmentProviderOffers } from "../itineraryDetails/itineraryDetails.tools";
import {
    computeItineraryOfferTotalPrice,
    computeRentalItineraryOfferLinkDisplay,
} from "../itineraryDetails/offerPricing/OfferPricingFacade";
import ItineraryReviewModal from "../ItineraryReviewModal";
import { resolveLegDetailsVisibleDuringResize } from "../panelResize/panelResize.tools";
import ItineraryCarSelectionPanelView from "./ItineraryCarSelectionPanelView";
import ItineraryDetailsPanelView from "./ItineraryDetailsPanelView";
import { getItineraryListViewMotionTargets, type ItineraryPanelViewDirection } from "./ItineraryItemMotion";
import { isCarpoolIncomplete } from "./itineraryList.tools";
import ItineraryListPanelView from "./ItineraryListPanelView";
import ItinerarySearchLoader from "./ItinerarySearchLoader";
import { useItineraryListDataFlow } from "./useItineraryListDataFlow.hook";
import { usePurchasedItineraryOpen } from "./usePurchasedItineraryOpen.hook";
import { useSavedItineraryListState } from "./useSavedItineraryListState.hook";

/**
 * Itinerary panel orchestrator: wires data-flow / saved-list hooks to panel views.
 *
 * @param props Home container props for the itinerary planner panel.
 * @returns Animated list / details / car-selection shell with search loader.
 */
const ItineraryList: React.FC<ContainerProps> = ( {
    selectedAddress,
    date,
    setDate,
    setIsEditedDate,
    setShowArrowTop,
    onResetPanelExpanded,
    onRestoreItineraryListPanelSnap,
    onRestoreItineraryDetailsPanelSnap,
    panelResizeLayout,
    selectedItinerary,
    setSelectedItinerary,
    ITakeMyCar,
    setITakeMyCar,
    departureSelectedAddress,
    ITakeMyCarData,
    setITakeMyCarData,
    carItineraries,
    setCarItineraries,
    selectedCarRouteId,
    setSelectedCarRouteId,
    setIsMerge,
    selectedInputs,
    setVisible,
    goItineraries,
    setGoItineraries,
    backupReturnItineraries,
    setBackupReturnItineraries,
    itineraries,
    setItineraries,
    selectedLeg,
    setSelectedLeg,
    focusMapOnPoint,
    isEuro,
    isDarkUi,
    isPricingPrefsPanelOpen,
    navigate,
    handleResetMap,
    debugItineraries,
    itineraryListContentMode,
    setItineraryListContentMode,
    activeSavedFilterTab,
    setActiveSavedFilterTab,
    onCloseSearchResults,
} ) => {
    const [ isCarPriceOpen, setIsCarPriceOpen ] = useState( false );
    const [ itineraryPanelViewDirection, setItineraryPanelViewDirection ] = useState<ItineraryPanelViewDirection>( "forward" );
    const [ carDetails, setCarDetails ] = useState<{
        "tollPrice":  number;
        "distance":   number;
        "totalPrice": number;
    } | null>( null );
    const [ reviewTarget, setReviewTarget ] = useState<ItineraryDTO | null>( null );
    const reducedMotion = useReducedMotion();

    const panelSnapTier = useEphemeralSelector<ItineraryPanelState, PanelSnapTier>(
        itineraryPanelStateSlice,
        ( panel ) => panel.panelSnapTier,
    );
    const dragHeightVh = useEphemeralSelector<ItineraryPanelState, number | null>(
        itineraryPanelStateSlice,
        ( panel ) => panel.dragHeightVh,
    );
    const isFollowingNavigation = useEphemeralSelector( navigationSessionSlice, ( session ) => session.isFollowingNavigation ) === true;
    const showNavigationAction = isNavigationEnabled();

    const {
        loading,
        loadingItinerary,
        loadingPricing,
        travelProgressRevealActive,
        skipListViewEnterAfterSearch,
        setSkipListViewEnterAfterSearch,
        offerStatusByItineraryId,
        setOfferStatusByItineraryId,
        selectedItineraryOfferStatus,
        itineraryPricingFromPrefs,
        wearCostPerKmChf,
        hasValidCoordinates,
        requestOfferForItinerary,
        itineraryDebugRelaunch,
        presentErrorToast,
        presentSuccessToast,
        loadTransitPage,
        transitPageLoading,
        transitPageExhaustedEarlier,
        transitPageExhaustedLater,
        transitPageNewBatch,
        transitPagePricingPendingIds,
    } = useItineraryListDataFlow( {
        selectedAddress,
        date,
        setDate,
        setIsEditedDate,
        departureSelectedAddress,
        selectedInputs,
        selectedItinerary,
        setSelectedItinerary,
        itineraries,
        setItineraries,
        setITakeMyCar,
        setITakeMyCarData,
        setShowArrowTop,
        onResetPanelExpanded,
        setVisible,
        itineraryListContentMode,
        setItineraryListContentMode,
        isPricingPrefsPanelOpen,
    } );

    const {
        isSavedListMode,
        showItineraryDetailsPrice,
        isSelectedItinerarySaved,
        displayedItineraries,
        pastDisplayedSavedEntries,
        currentDisplayedSavedEntries,
        upcomingDisplayedSavedEntries,
        savedEmptyStateVariant,
        shouldRenderSavedListShell,
        itineraryDetailsDepartureAddress,
        itineraryDetailsDestinationAddress,
        realtimeOverlayById,
        handleSavedItineraryToggle,
        handleRemoveSavedItinerary,
        handleCloseList,
        handleSavedListExitComplete,
    } = useSavedItineraryListState( {
        itineraryListContentMode,
        setItineraryListContentMode,
        activeSavedFilterTab,
        selectedItinerary,
        setSelectedItinerary,
        itineraries,
        departureSelectedAddress,
        selectedAddress,
        selectedInputs,
        setVisible,
        onCloseSearchResults,
        presentErrorToast,
    } );

    /**
     * Details view prefers the ephemeral live DTO when the overlay has an entry
     * for the selected id (scheduled snapshot remains in IndexedDB).
     */
    const liveSelectedItinerary = useMemo( (): ItineraryDTO | null => {
        if ( selectedItinerary === null || selectedItinerary === undefined ) {
            return null;
        }

        return realtimeOverlayById[selectedItinerary.id] ?? selectedItinerary;
    }, [
        realtimeOverlayById,
        selectedItinerary,
    ] );

    const isSelectedItineraryPurchased = useMemo(
        () => liveSelectedItinerary !== null && hasPurchasedSegments( liveSelectedItinerary ),
        [ liveSelectedItinerary ],
    );

    const handleItineraryLongPress = useCallback( ( itinerary: ItineraryDTO ): void => {
        if ( !__IS_DEV__ ) {
            return;
        }

        setReviewTarget( itinerary );
    }, [] );

    /**
     * Hide leg details when going back to the list.
     */
    const effectiveLegDetails = ITakeMyCar || selectedItinerary === null
        ? false
        : resolveLegDetailsVisibleDuringResize(
            panelSnapTier,
            dragHeightVh,
            panelResizeLayout.chromeMinHeightVh,
        );

    const itineraryDetailsChromeData = useMemo( () => {
        if ( liveSelectedItinerary === null ) {
            return null;
        }

        const hasRentalPricingSegment = ( liveSelectedItinerary.pricingSegments ?? [] ).some( ( segment ) => segment.provider?.id === SegmentationProviderId.rental_motis );

        return {
            "computedOfferTotalPrice":          computeItineraryOfferTotalPrice( liveSelectedItinerary, itineraryPricingFromPrefs ),
            "showIncompleteOfferTotalFootnote": itineraryHasNullSegmentProviderOffers( liveSelectedItinerary, { "swissReductionCard": itineraryPricingFromPrefs.swissReductionCard } ),
            "rentalItineraryOfferLink":         hasRentalPricingSegment
                ? computeRentalItineraryOfferLinkDisplay( liveSelectedItinerary, itineraryPricingFromPrefs )
                : null,
        };
    }, [ itineraryPricingFromPrefs, liveSelectedItinerary ] );

    // Select itinerary, restore details snap, prime GPS segments, kick off offer when needed.
    const handleItineraryClick = useCallback( ( itinerary: ItineraryDTO ): void => {
        if ( isCarpoolIncomplete( itinerary ) ) {
            return;
        }

        setSelectedItinerary( itinerary );
        setItineraryPanelViewDirection( "forward" );
        setShowArrowTop( true );
        onRestoreItineraryDetailsPanelSnap();
        setIsMerge( true );
        if ( itinerary.offer !== undefined ) {
            setOfferStatusByItineraryId( ( previousStatusById ) => ( {
                ...previousStatusById,
                [itinerary.id]: "success",
            } ) );
        } else if ( offerStatusByItineraryId[itinerary.id] === undefined ) {
            setOfferStatusByItineraryId( ( previousStatusById ) => ( {
                ...previousStatusById,
                [itinerary.id]: "idle",
            } ) );
        }

        const segments = convertItineraryToSegments( itinerary );
        gpsPredictionService.setRouteSegments( segments );

        if ( !isSavedListMode && itinerary.offer === undefined ) {
            requestOfferForItinerary( itinerary );
        }
    }, [
        isSavedListMode,
        offerStatusByItineraryId,
        onRestoreItineraryDetailsPanelSnap,
        requestOfferForItinerary,
        setIsMerge,
        setOfferStatusByItineraryId,
        setSelectedItinerary,
        setShowArrowTop,
    ] );

    /**
     * Opens the freshly persisted purchased entry exactly like a saved-list
     * click, after switching the panel content to the saved list so the
     * details chrome keeps its saved semantics (no price, back to saved list).
     */
    const openPurchasedItinerary = useCallback( ( itinerary: ItineraryDTO ): void => {
        setItineraryListContentMode( "saved" );
        handleItineraryClick( itinerary );
    }, [ handleItineraryClick, setItineraryListContentMode ] );

    usePurchasedItineraryOpen( openPurchasedItinerary );

    const hasSelectedItinerary = liveSelectedItinerary !== null;
    const activeItineraryPanelViewKey = !hasSelectedItinerary && !ITakeMyCar
        ? "list"
        : !ITakeMyCar && hasSelectedItinerary
            ? "details"
            : "car";

    const activeItineraryPanelView = !hasSelectedItinerary && !ITakeMyCar
        ? (
            <ItineraryListPanelView
                ITakeMyCarData={ITakeMyCarData}
                activeSavedFilterTab={activeSavedFilterTab}
                carDetails={carDetails}
                currentDisplayedSavedEntries={currentDisplayedSavedEntries}
                debugItineraries={debugItineraries}
                displayedItineraries={displayedItineraries}
                hasValidCoordinates={hasValidCoordinates}
                isCarPriceOpen={isCarPriceOpen}
                isDarkUi={isDarkUi === true}
                isEuro={isEuro}
                isListDebugActionDisabled={itineraryDebugRelaunch.isListActionDisabled}
                isSavedListMode={isSavedListMode}
                itinerariesCount={itineraries.length}
                itineraryListContentMode={itineraryListContentMode}
                loading={loading}
                loadingItinerary={loadingItinerary}
                loadingPricing={loadingPricing}
                onActiveSavedFilterTabChange={setActiveSavedFilterTab}
                onCloseList={handleCloseList}
                onItineraryClick={handleItineraryClick}
                onItineraryLongPress={__IS_DEV__ ? handleItineraryLongPress : undefined}
                onLoadTransitPage={loadTransitPage}
                onRelaunchListSegmentation={itineraryDebugRelaunch.handleRelaunchListSegmentation}
                onRemoveSavedItinerary={handleRemoveSavedItinerary}
                onResetPanelExpanded={onResetPanelExpanded}
                onSavedListExitComplete={handleSavedListExitComplete}
                panelResizeLayout={panelResizeLayout}
                pastDisplayedSavedEntries={pastDisplayedSavedEntries}
                pricingPreferences={itineraryPricingFromPrefs}
                savedEmptyStateVariant={savedEmptyStateVariant}
                selectedInputs={selectedInputs}
                setCarDetails={setCarDetails}
                setITakeMyCar={setITakeMyCar}
                setIsCarPriceOpen={setIsCarPriceOpen}
                shouldRenderSavedListShell={shouldRenderSavedListShell}
                transitPageExhaustedEarlier={transitPageExhaustedEarlier}
                transitPageExhaustedLater={transitPageExhaustedLater}
                transitPageLoading={transitPageLoading}
                transitPageNewBatch={transitPageNewBatch}
                transitPagePricingPendingIds={transitPagePricingPendingIds}
                travelProgressRevealActive={travelProgressRevealActive}
                upcomingDisplayedSavedEntries={upcomingDisplayedSavedEntries}
                wearCostPerKmChf={wearCostPerKmChf}
            />
        )
        : !ITakeMyCar && liveSelectedItinerary !== null
            ? (
                <ItineraryDetailsPanelView
                    debugItineraries={debugItineraries}
                    departureSelectedAddress={itineraryDetailsDepartureAddress}
                    focusMapOnPoint={focusMapOnPoint}
                    handleResetMap={handleResetMap}
                    isDarkUi={isDarkUi === true}
                    isDetailsDebugActionDisabled={itineraryDebugRelaunch.isDetailsActionDisabled}
                    isEuro={isEuro}
                    isFollowingNavigation={isFollowingNavigation}
                    isPurchasedItinerary={isSelectedItineraryPurchased}
                    isSelectedItinerarySaved={isSelectedItinerarySaved}
                    itineraryDetailsChromeData={itineraryDetailsChromeData}
                    legDetails={effectiveLegDetails}
                    offerStatus={selectedItineraryOfferStatus}
                    onNavigate={navigate}
                    onRelaunchOffer={itineraryDebugRelaunch.handleRelaunchDetailsOffer}
                    onRelaunchSegmentationAndOffer={itineraryDebugRelaunch.handleRelaunchDetailsSegmentationAndOffer}
                    onResetPanelExpanded={onResetPanelExpanded}
                    onRestoreItineraryListPanelSnap={onRestoreItineraryListPanelSnap}
                    onSaveToggle={handleSavedItineraryToggle}
                    panelResizeLayout={panelResizeLayout}
                    pricingPreferences={itineraryPricingFromPrefs}
                    selectedAddress={itineraryDetailsDestinationAddress}
                    selectedItinerary={liveSelectedItinerary}
                    selectedLeg={selectedLeg}
                    setBackupReturnItineraries={setBackupReturnItineraries}
                    setCarItineraries={setCarItineraries}
                    setGoItineraries={setGoItineraries}
                    setITakeMyCar={setITakeMyCar}
                    setItineraryPanelViewDirection={setItineraryPanelViewDirection}
                    setSelectedItinerary={setSelectedItinerary}
                    setSelectedLeg={setSelectedLeg}
                    setShowArrowTop={setShowArrowTop}
                    showNavigationAction={showNavigationAction}
                    showPrice={showItineraryDetailsPrice}
                />
            )
            : (
                <ItineraryCarSelectionPanelView
                    ITakeMyCarData={ITakeMyCarData}
                    backupReturnItineraries={backupReturnItineraries}
                    carDetails={carDetails}
                    carItineraries={carItineraries}
                    date={date}
                    goItineraries={goItineraries}
                    handleResetMap={handleResetMap}
                    isCarPriceOpen={isCarPriceOpen}
                    isDarkUi={isDarkUi === true}
                    isEuro={isEuro}
                    onCloseCarPriceDetail={() => {
                        setIsCarPriceOpen( false );
                    }}
                    onOpenCarPriceDetail={() => {
                        setIsCarPriceOpen( true );
                    }}
                    onResetPanelExpanded={onResetPanelExpanded}
                    panelResizeLayout={panelResizeLayout}
                    selectedCarRouteId={selectedCarRouteId}
                    selectedInputs={selectedInputs}
                    setBackupReturnItineraries={setBackupReturnItineraries}
                    setCarItineraries={setCarItineraries}
                    setGoItineraries={setGoItineraries}
                    setITakeMyCar={setITakeMyCar}
                    setItineraryPanelViewDirection={setItineraryPanelViewDirection}
                    setSelectedCarRouteId={setSelectedCarRouteId}
                />
            );

    const itineraryListViewMotionTargets = getItineraryListViewMotionTargets( itineraryPanelViewDirection, reducedMotion );
    const listViewInitial = activeItineraryPanelViewKey === "list" && skipListViewEnterAfterSearch
        ? false
        : itineraryListViewMotionTargets.initial;

    return (
        <>
            <AnimatePresence
                initial={false}
                mode="wait"
            >
                {loading && !isSavedListMode
                    ? (
                        <motion.div
                            animate={{ "opacity": 1 }}
                            exit={{
                                "opacity":    0,
                                "transition": resolveUiEaseOut( reducedMotion, UI_MOTION.duration.fastMs ),
                            }}
                            initial={{ "opacity": 1 }}
                            key="itinerary-search-loading"
                        >
                            <ItinerarySearchLoader />
                        </motion.div>
                    )
                    : (
                        <motion.div
                            animate={itineraryListViewMotionTargets.animate}
                            className="flex min-h-0 flex-1 flex-col"
                            exit={itineraryListViewMotionTargets.exit}
                            id={activeItineraryPanelViewKey === "list"
                                ? "itinerary-list-view"
                                : activeItineraryPanelViewKey === "details"
                                    ? "itinerary-details-view"
                                    : "itinerary-car-selection-view"}
                            initial={listViewInitial}
                            key={activeItineraryPanelViewKey}
                            onAnimationComplete={() => {
                                setSkipListViewEnterAfterSearch( false );
                            }}
                            transition={itineraryListViewMotionTargets.transition}
                        >
                            {activeItineraryPanelView}
                        </motion.div>
                    )}
            </AnimatePresence>
            {__IS_DEV__ && (
                <ItineraryReviewModal
                    isDarkUi={isDarkUi === true}
                    isOpen={reviewTarget !== null}
                    itinerary={reviewTarget}
                    onClose={() => {
                        setReviewTarget( null );
                    }}
                    onError={presentErrorToast}
                    onSaved={() => {
                        setReviewTarget( null );
                        presentSuccessToast( "Review enregistrée" );
                    }}
                />
            )}
        </>
    );
};

export default ItineraryList;
